import cloneDeep from 'lodash/cloneDeep';
import {Component} from 'panel';

import {CustomPropertyBuilderErrors, CustomPropertyBuilderState} from './types';
import {CustomProperty} from 'common/report/stores/custom-properties';
import CustomPropertyCompleter from './completer';
import {isPersistedCustomProperty, getInlineCustomPropertyId} from './util';
import customPropertiesStore from 'common/report/stores/custom-properties';
import customPropertyClient from 'common/report/queries/app-api/custom-properties-client';
import {ResponseError} from 'common/util/api-client';
import customPropertiesValidateClient from 'common/report/queries/query-api/custom-properties-validate-client';
import {emitTrackEvent} from '../../util/dom';
import {registerMPElement} from 'common/util/register-element';
import {QUERY_BUILDER_RESOURCE_TYPE_MAPPING} from 'common/widgets/property-filter-menu/models/insights-filter';

import ace from 'brace';
import 'brace/ext/language_tools';
import template from './index.jade';
import css from './index.styl?inline';
import 'common/util/ace/patches';
import 'common/util/ace/theme/mixpanel';
import arbselectorDefs from 'common/util/tern/definitions/arb-selector.json';
import {
  SelectorTypeDefinitions,
  SelectorType,
  FilterOperator,
  QueryEntry,
  QueryBehaviorEntry,
  QueryPropertyEntry,
} from '../query-event-selector/types';
import {RowTypeOption} from '../query-events-section/types';
import {ResourceType} from '../property-filter-menu/models/types';
import pick from 'lodash/pick';
import {EVENT_PROPERTIES, EventItem} from '../query-block/events-menu/types';
import {PropertyItem} from '../query-block/properties-menu/types';
import {getAlphaLabelFromIndex, getIndexFromAlphaLabel} from 'common/util';

const {Autocomplete} = ace.acequire(`ace/autocomplete`);
const range = ace.acequire(`ace/range`);

const MAX_COMPOSED_PROPERTIES = 5;
const DISPATCHED_EVENTS = {
  CANCEL: `cancel`,
  APPLY: `apply`,
  DELETE: `delete`,
};
const EXAMPLES = {
  'Resize range': `// Select a numeric property above for this use-case.
// The selected property is referenced as A below and, in this example,
// A is assumed to be the time a user took to do an action, in minutes.
// This formula groups durations based on specified ranges.
IFS(
  A < 60, "Less than 1 hour",
  A < 120, "More than 1 hour, less than 2 hours",
  A < 240, "More than 2 hours, less than 4 hours",
  A < 360, "More than 4 hours, less than 6 hours",
  A >= 360, "More than 6 hours"
)`,
  'Merge values': `// Select a string property above for this use-case.
// The selected property is referenced as A below and, in this example,
// A is assumed to be a string with values like "iOS 9", "iOS 10",
// "Mozilla", "Firefox", that can be grouped into larger categories.
IFS(
  "iOS" in A, "Apple",
  "Mozilla" in A or "Firefox" in A, "Firefox",

  // Since this last case is always TRUE, if none of the above
  // conditions are met, this formula will evaluate to the original
  // value of A
  TRUE, A
)`,
  'Extract regex': `// Select a string property above for this use-case.
// The selected property is referenced as A below and, in this example,
// A is assumed to be a URL (e.g. "https://mixpanel.com/report/123").
// This formula extracts "mixpanel.com" from the URL using a regular
// expression. There is also a REGEX_MATCH() function.
REGEX_EXTRACT(A, "https?:\\/\\/([^\\/]+)", 1)`,
  'Days since sign-up': `// Select two datetime properties above for this use-case.
// The selected properties are referenced as A and B below. In this
// example a good selection for A would be a user property that
// records the user's sign up date, and a good selection for B would
// be the "Date" event property. With these selections this custom
// property will evaluate to the number of days after sign up that a
// user performed each event.
DATEDIF(A, B, "D")`,
};
const DEFAULT_FORMULA = `// Select a property above and use it in a formula here. You can
// reference selected properties by their letter name, the first
// property you select will be 'A', the second will be 'B', etc.
// If you need examples to get started try the 'Insert Example' menu to
// see sample formulas for common use cases.
A`;
const HELP_DOC = `https://help.mixpanel.com/hc/en-us/articles/360030848432`;

const COMPONENT_NAME = `mp-custom-property-modal`;

const CUSTOM_PROPERTY_RESOURCE_TYPES = {
  [ResourceType.Event]: QUERY_BUILDER_RESOURCE_TYPE_MAPPING[ResourceType.Event],
  [ResourceType.User]: QUERY_BUILDER_RESOURCE_TYPE_MAPPING[ResourceType.User],
};

const BEHAVIOR_QUERY_ENTRY_FIELDS = [
  `event`,
  `dateRange`,
  `dataGroupId`,
  `filters`,
  `filtersOperator`,
  `property`,
  `aggregationOperator`,
];

export const TRACKING_EVENTS = {
  SAVE: `Custom Property - save`,
  INSERT_EXAMPLE: `Custom Property - insert example`,
  CANCEL: `Custom Property - cancel`,
};

let autocompleteSingleton = null;

export default registerMPElement(
  COMPONENT_NAME,
  class CustomPropertyBuilder extends Component<CustomPropertyBuilderState> {
    textarea: Editor;
    idleTimeout: ReturnType<typeof setTimeout>;
    editor: any;
    customPropertyCompleter: CustomPropertyCompleter;

    constructor() {
      super();
    }

    static get dispatchedEvents() {
      return Object.values(DISPATCHED_EVENTS);
    }

    static get attrsSchema() {
      return {
        ...super.attrsSchema,
        'default-name': `string`,
        'enable-behavioral-properties': {type: `boolean`, default: false},
        property: {
          type: `json`,
          default: null,
          description: `the inline or persisted custom property to render.`,
        },
        'project-id': `number`,
        'user-id': `number`,
        open: `boolean`,
        'track-name': {
          type: `string`,
          description: `If specified, dispatches '[mp-custom-property-modal] <event>' bubbled events that can be listened for tracking with "Report name" as a property`,
        },
        'workspace-id': `number`,
      };
    }

    attributeChangedCallback(attr, oldVal, newVal) {
      super.attributeChangedCallback(attr, oldVal, newVal);
      if (attr === `property`) {
        const property = this.attr(attr) || {};
        const {customPropertyId} = property;
        let {customProperty, resourceType} = property;
        if (!resourceType) {
          if (isPersistedCustomProperty(customPropertyId) && !customProperty) {
            customProperty = customPropertiesStore.value[customPropertyId];
            // make a copy to not modify the store directly
            customProperty = cloneDeep(customProperty);
          }
          if (customProperty) {
            resourceType = customProperty.resourceType;
          }
        }
        if (!customProperty) {
          customProperty = this.newCustomProperty();
        }
        // custom properties use 'events' and 'people' instead of 'event' and 'user'
        // TODO (safdar): change this to DTRT when/if custom properties are migrated to the QueryEntry format
        if (Object.keys(CUSTOM_PROPERTY_RESOURCE_TYPES).includes(resourceType)) {
          resourceType = CUSTOM_PROPERTY_RESOURCE_TYPES[resourceType];
        }
        this.update({
          customPropertyId,
          customProperty,
          resourceType,
          toCreate: null,
        });
        this.updateQueryEntriesFromComposedProperties();
      }
    }

    connectedCallback() {
      super.connectedCallback();
      // this is hacky, but ace editor injects the style tags into the global space so we grab them
      // from there and inject the stylesheets into the style tag in the shadowdom we create here
      const aceCss = [`ace_editor.css`, `ace-tm`, `ace-mixpanel`]
        .map((id) => document.getElementById(id).innerHTML)
        .join(` `);
      const styleTag = document.createElement(`style`);
      styleTag.innerHTML = aceCss;
      this.el.appendChild(styleTag);
    }

    get config() {
      return {
        ...super.config,
        css,
        useShadowDom: true,
        template,
        defaultState: {
          activePropertyIndex: null,
          customPropertyId: null,
          customProperty: this.newCustomProperty(),
          errors: {},
          queryEntries: [] as QueryEntry[],
          toDelete: false,
          autocompleteOpen: false,
          autocompletePopup: null,
          currentAutocompleteLine: null,
          toCreate: null,
        },
        helpers: {
          MAX_COMPOSED_PROPERTIES,
          tooltipClicked: () => window.open(HELP_DOC, ``).focus(),
          updateName: (ev) => {
            this.updateField(`name`, ev.target.value, Boolean);
          },
          updateDescription: (ev) => {
            const customProperty = {...this.state.customProperty, description: ev.target.value};
            this.update({customProperty});
          },
          getName: () => this.state.customProperty.name,
          getDescription: () => this.state.customProperty.description,
          getApplyButtonText: () => (this.helpers.isEdit() && this.helpers.isPersisted() ? `Update` : `Apply`),
          isEdit: () => {
            const property = this.attr(`property`);
            return property && (property.customPropertyId || property.customProperty);
          },
          isPersisted: () => isPersistedCustomProperty(this.state.customPropertyId),
          canEditVisibility: () => this.state.customProperty.canUpdateVisible,
          canEditLocked: () => this.state.customProperty.canUpdateLocked,
          getHeaderText: () => {
            if (this.helpers.isPersisted()) {
              const resourceTypeName = this.helpers.getResourceTypeText()
                ? this.helpers.getResourceTypeText().toLowerCase()
                : null;
              return [`Update`, resourceTypeName, `property`].join(` `);
            } else {
              return `Apply formula`;
            }
          },
          getResourceType: () => this.state.resourceType,
          getResourceTypeText: () => {
            return {
              [CUSTOM_PROPERTY_RESOURCE_TYPES[ResourceType.User]]: `Profile`,
              [CUSTOM_PROPERTY_RESOURCE_TYPES[ResourceType.Event]]: `Event`,
            }[this.helpers.getResourceType()];
          },
          getModalAttributes: () => ({
            open: this.attr(`open`),
            closeable: true,
            'grow-to-content': true,
            'modal-type': `modal`,
            'vertical-alignment': `top`,
            scrollable: true,
          }),
          modalStateChange: (ev) => {
            if (ev.detail && ev.detail.state === `closed`) {
              this.close();
            }
          },
          getPersistedCustomPropertyNames: () => {
            return Object.entries(customPropertiesStore.value)
              .filter((prop) => isPersistedCustomProperty(prop[0]))
              .map((prop) => prop[1].name);
          },
          getComposedProperties: () => this.getComposedProperties(),
          getDisplayFormula: () => this.state.customProperty.displayFormula,
          closeModal: () => this.close(),
          setToDelete: () => this.update({toDelete: true}),
          deleteProperty: () => {
            // TODO: handle network blip, etc, when deleting a property
            this.deleteProperty(this.state.customPropertyId);
            this.dispatchEvent(new CustomEvent(DISPATCHED_EVENTS.DELETE));
          },
          handleDeleteConfirmChange: (visibility) => {
            if (visibility !== `open`) {
              this.update({toDelete: false});
            }
          },
          handleVisibilityHiddenChange: (ev) => {
            const customProperty: CustomProperty = {
              ...this.state.customProperty,
              isVisible: !ev.detail.value,
            };
            this.update({customProperty});
          },
          handleVisibilityLockedChange: (ev) => {
            const customProperty: CustomProperty = {
              ...this.state.customProperty,
              isLocked: ev.detail.value,
            };
            this.update({customProperty});
          },
          getExampleOptions: () => Object.keys(EXAMPLES),
          handleInsertExample: (ev) => {
            let newContent = EXAMPLES[ev.detail.value];
            const currentContent = this.state.customProperty.displayFormula.trim();
            if (currentContent && !this.isExampleFormula(currentContent)) {
              const currentLines = currentContent.match(/[^\r\n]+/g);
              const commentedOut = currentLines.map((line) => `// ` + line).join(`\n`);
              newContent += `\n\n` + commentedOut;
            }
            this.trackWithState(
              TRACKING_EVENTS.INSERT_EXAMPLE,
              this.state.customPropertyId,
              this.state.customProperty,
              false,
              {},
              {
                Example: ev.detail.value,
              },
            );
            this.editor.setValue(newContent, -1);
          },
          applyProperty: async (toCreate) => {
            let errors: CustomPropertyBuilderErrors = {};
            if (!this.helpers.getName()) {
              errors.name = `Required`;
            }
            if (!this.helpers.getDisplayFormula()) {
              errors.displayFormula = `Required`;
            }
            if (Object.keys(this.getComposedProperties()).length === 0) {
              errors.composedProperties = `Required`;
            }
            const composedPropertiesError = this.validateComposedProperties();
            if (composedPropertiesError !== null) {
              errors.composedProperties = composedPropertiesError;
            }
            if (Object.entries(errors).length > 0) {
              this.trackWithState(
                TRACKING_EVENTS.SAVE,
                this.state.customPropertyId,
                this.state.customProperty,
                toCreate,
                errors,
              );
              this.update({errors});
              return;
            }

            let customProperty = {
              ...this.state.customProperty,
              composedProperties: this.getComposedProperties(),
              resourceType: this.state.resourceType,
            };

            let result;
            let storeUpdate = {};
            const projectId = this.attr(`project-id`);
            let {customPropertyId} = this.state;
            try {
              if (isPersistedCustomProperty(customPropertyId)) {
                // update
                result = await customPropertiesStore.put(customPropertyId, {
                  projectId,
                  customProperty,
                  customPropertyId,
                });
              } else {
                // inline
                const response = await customPropertyClient.query({
                  projectId,
                  customProperty,
                  validate: true,
                  workspaceId: this.attr(`workspace-id`),
                });
                if (response.status === `error`) {
                  throw new ResponseError(response);
                } else {
                  result = response.results;
                }
              }
            } catch (err) {
              errors = {[err.response.field]: err.response.error};
              this.trackWithState(TRACKING_EVENTS.SAVE, customPropertyId, customProperty, toCreate, errors);
              this.update({errors});
              return;
            }

            const propertyType = result && result.propertyType;
            customProperty = {...customProperty, propertyType};

            if (!result || !result.customPropertyId) {
              // inline, we give it a session only UID if none has been given before
              customPropertyId = getInlineCustomPropertyId(customPropertyId);
              // and manually set it to the cache
              storeUpdate = {[customPropertyId]: customProperty as CustomProperty};
            }

            if (toCreate) {
              this.update({
                toCreate: customProperty,
              });
            } else {
              customPropertiesStore.update(storeUpdate);
              this.trackWithState(TRACKING_EVENTS.SAVE, customPropertyId, customProperty, toCreate, {});
              this.dispatchEvent(
                new CustomEvent(DISPATCHED_EVENTS.APPLY, {
                  detail: {
                    property: {
                      customProperty,
                      customPropertyId,
                      resourceType: this.state.resourceType,
                      propertyType,
                      type: propertyType,
                      value: name,
                    },
                  },
                }),
              );
            }
          },
          handleCreate: async (ev) => {
            const {name, description, hidden, locked} = ev.detail.value;
            const customProperty: CustomProperty = {
              ...this.state.toCreate,
              name,
              description,
              isVisible: !hidden,
              isLocked: locked,
            };
            const projectId = this.attr(`project-id`);
            const result = await customPropertiesStore.post({
              projectId,
              customProperty,
              workspaceId: this.attr(`workspace-id`),
            });
            if (result && result.customPropertyId) {
              const customPropertyId = result.customPropertyId;
              if (this.state.customPropertyId && !isPersistedCustomProperty(this.state.customPropertyId)) {
                // we just saved an inline customProperty to a persisted one
                customPropertiesStore.update({
                  [this.state.customPropertyId]: {
                    ...customPropertiesStore.value[this.state.customPropertyId],
                    newCustomPropertyId: customPropertyId,
                  },
                });
              }
              this.trackWithState(TRACKING_EVENTS.SAVE, customPropertyId, customProperty, true, {});
              this.dispatchEvent(
                new CustomEvent(DISPATCHED_EVENTS.APPLY, {
                  detail: {
                    property: {
                      customProperty,
                      customPropertyId,
                      resourceType: this.state.resourceType,
                      propertyType: customProperty.propertyType,
                      propertyDefaultType: customProperty.propertyType,
                      type: customProperty.propertyType,
                      value: name,
                    },
                  },
                }),
              );
            }
          },
          handleCancelCreate: () => {
            this.update({toCreate: null});
          },
          updateDisplayFormula: (newFormula) => {
            this.updateField(`displayFormula`, newFormula, Boolean);
          },
          handleEditorConnect: (ev) => {
            this.editor = ev.detail.editor;
            this.customPropertyCompleter = ev.detail.customPropertyCompleter;
            this.customPropertyCompleter.setComposedProperties(this.attr(`project-id`), this.getComposedProperties());
            this.update({
              autocompletePopup: ev.detail.autocompletePopup,
            });
          },
          handleEditorAutocompleteChange: (ev) => {
            const updates = {};
            if (ev.detail.autocompleteOpen !== undefined) {
              updates[`autocompleteOpen`] = ev.detail.autocompleteOpen;
            }
            if (ev.detail.currentAutocompleteLine !== undefined) {
              updates[`currentAutocompleteLine`] = ev.detail.currentAutocompleteLine;
            }
            if (ev.detail.autocompleteOpen || this.state.autocompleteOpen) {
              if (ev.detail.menuTooltipData !== undefined) {
                updates[`tooltipData`] = ev.detail.menuTooltipData;
              }
            } else {
              if (ev.detail.cursorTooltipData !== undefined) {
                updates[`tooltipData`] = ev.detail.cursorTooltipData;
              }
            }
            this.update(updates);
          },
          handleValueChange: (ev) => {
            ev.stopPropagation();
            this.helpers.updateDisplayFormula(ev.detail.value);

            this.textarea.editor.getSession().setAnnotations([]);
            for (const markerId of Object.keys(this.textarea.editor.getSession().getMarkers(false))) {
              this.textarea.editor.getSession().removeMarker(markerId);
            }
            clearTimeout(this.idleTimeout);
            this.idleTimeout = null;
            if (
              this.state.customProperty.displayFormula !== `` &&
              !this.isExampleFormula(this.state.customProperty.displayFormula)
            ) {
              const projectId = this.getAttribute(`project-id`);
              const selector = this.state.customProperty.displayFormula;
              const variableSubstitutions = Object.entries(this.getComposedProperties()).reduce(
                (result, arr) => Object.assign(result, {[arr[0]]: `unused`}),
                {},
              );
              this.idleTimeout = setTimeout(() => {
                (async () => {
                  const resp = await customPropertiesValidateClient.query({
                    projectId,
                    selector,
                    variableSubstitutions,
                  });
                  // if the formula has changed since we issued our validation query then our
                  // results are no longer useful, another query will already have been issued
                  const currProps = Object.entries(this.getComposedProperties()).reduce(
                    (result, arr) => Object.assign(result, {[arr[0]]: `unused`}),
                    {},
                  );
                  if (
                    resp.syntax_errors &&
                    selector === this.state.customProperty.displayFormula &&
                    JSON.stringify(Object.keys(variableSubstitutions)) === JSON.stringify(Object.keys(currProps))
                  ) {
                    const annotations = [];
                    for (const err of resp.syntax_errors) {
                      annotations.push({
                        row: err[`first_line`],
                        column: err[`first_column`],
                        type: `error`,
                        text: `\u2022 ` + err[`message`],
                      });
                      this.textarea.editor
                        .getSession()
                        .addMarker(
                          new range.Range(
                            err[`first_line`],
                            err[`first_column`],
                            err[`last_line`],
                            err[`last_column`] + 1,
                          ),
                          `ace-highlight-syntax-error`,
                          `text`,
                          false,
                        );
                    }
                    this.textarea.editor.getSession().setAnnotations(annotations);
                  }
                })();
              }, 250);
            }
          },
          handleEditorElementInsert: (vnode) => {
            this.textarea = vnode.elm;
            requestAnimationFrame(() => this.textarea.focus());
          },
          getQueryEntries: () => this.getQueryEntries(),
          getQueryEventsSectionSelectorTypes: () => {
            switch (this.helpers.getResourceType()) {
              case CUSTOM_PROPERTY_RESOURCE_TYPES[ResourceType.Event]:
                return [SelectorTypeDefinitions.EventOrUserProperty] as RowTypeOption[];
              case CUSTOM_PROPERTY_RESOURCE_TYPES[ResourceType.User]:
                if (this.attr(`enable-behavioral-properties`)) {
                  return [SelectorTypeDefinitions.UserProperty, SelectorTypeDefinitions.Behavior] as RowTypeOption[];
                }
                return [SelectorTypeDefinitions.UserProperty] as RowTypeOption[];
              default:
                return [] as RowTypeOption[];
            }
          },
          handleQueryEventsSectionUpdate: (ev: CustomEvent) => {
            this.updateQueryEntries(ev.detail.eventEntries);
            this.updateComposedPropertiesFromQueryEntries();
          },
        },
      };
    }

    close() {
      this.trackWithState(TRACKING_EVENTS.CANCEL, this.state.customPropertyId, this.state.customProperty, false, {});
      this.dispatchEvent(new CustomEvent(DISPATCHED_EVENTS.CANCEL));
    }

    getComposedProperties() {
      return this.state.customProperty.composedProperties;
    }

    isExampleFormula(formula: string): boolean {
      if (formula === DEFAULT_FORMULA) {
        return true;
      }
      for (const exampleContent of Object.values(EXAMPLES)) {
        if (formula === exampleContent) {
          return true;
        }
      }
      return false;
    }

    updateComposedProperties(composedProperties) {
      this.customPropertyCompleter.setComposedProperties(this.attr(`project-id`), composedProperties);
      this.updateField(`composedProperties`, composedProperties, (value) => Object.entries(value).length > 1);
    }

    validateComposedProperties() {
      const composedProperties = this.getComposedProperties();
      for (const letter of Object.keys(composedProperties)) {
        const property = composedProperties[letter];
        if (property.behavior && !property.behavior.dateRange) {
          return `Date range required for variable ${letter}`;
        }
        // TODO: should check for recursive references within behaviors as well
        if (
          property.customPropertyId &&
          this.state.customPropertyId &&
          property.customPropertyId === this.state.customPropertyId
        ) {
          return `Variable ${letter} refers to this property. Properties cannot refer to themselves.`;
        }
        if (
          property.customPropertyId &&
          this.state.customProperty.referencedBy &&
          this.state.customProperty.referencedBy.includes(property.customPropertyId)
        ) {
          return `Variable ${letter} cannot refer to property '${property.label}' because '${property.label}' already refers to this property.`;
        }
      }
      return null;
    }

    getQueryEntries() {
      return this.state.queryEntries;
    }

    updateQueryEntries(queryEntries: QueryEntry[]) {
      this.update({queryEntries});
    }

    updateComposedPropertiesFromQueryEntries() {
      const composedProperties = {};
      this.getQueryEntries().forEach((entry, index) => {
        let property;
        if (entry.type === SelectorTypeDefinitions.Behavior) {
          property = {
            resourceType: ResourceType.User,
            behavior: pick(entry, BEHAVIOR_QUERY_ENTRY_FIELDS),
          };
        } else {
          property = {...entry.property, resourceType: entry.property.resourceType};
        }
        composedProperties[getAlphaLabelFromIndex(index)] = property;
      });
      this.updateComposedProperties(composedProperties);
    }

    updateQueryEntriesFromComposedProperties() {
      const composedProperties = this.getComposedProperties();
      const letters = Object.keys(composedProperties).sort(
        (a: string, b: string) => getIndexFromAlphaLabel(a) - getIndexFromAlphaLabel(b),
      );
      const queryEntries: QueryEntry[] = letters.map((letter) => {
        const property = composedProperties[letter];
        let entry: QueryEntry;
        if (property.behavior) {
          entry = {
            ...pick(property.behavior, BEHAVIOR_QUERY_ENTRY_FIELDS),
            type: SelectorTypeDefinitions.Behavior,
          } as QueryBehaviorEntry;
        } else {
          let type: SelectorType;
          switch (this.state.resourceType) {
            case CUSTOM_PROPERTY_RESOURCE_TYPES[ResourceType.Event]:
              type = SelectorTypeDefinitions.EventOrUserProperty;
              break;
            case CUSTOM_PROPERTY_RESOURCE_TYPES[ResourceType.User]:
              type = SelectorTypeDefinitions.UserProperty;
              break;
            default:
              throw new Error(`Invalid resourceType ${property.resourceType} on composed property ${letter}`);
          }
          entry = {
            type,
            filters: [],
            filtersOperator: FilterOperator.And,
            event: pick(property, EVENT_PROPERTIES) as EventItem,
            property: property as PropertyItem,
          } as QueryPropertyEntry;
        }
        return entry;
      });
      this.updateQueryEntries(queryEntries);
    }

    updateField(field, value, toClearErrorFunc) {
      const customProperty = {...this.state.customProperty, [field]: value};
      const {errors} = this.state;
      if (toClearErrorFunc(value) && errors[field]) {
        errors[field] = null;
      }
      this.update({customProperty, errors});
    }

    deleteProperty(customPropertyId) {
      customPropertiesStore.delete(customPropertyId, {
        projectId: this.attr(`project-id`),
        customPropertyId,
      });
    }

    newCustomProperty() {
      return {
        name: this.attr(`default-name`),
        description: ``,
        displayFormula: ``,
        composedProperties: {},
      } as CustomProperty;
    }

    track(event, props) {
      emitTrackEvent(this, {
        eventName: event,
        eventPrefix: COMPONENT_NAME,
        props: {
          ...props,
          project_id: this.attr(`project-id`),
          tab: this.attr(`track-name`),
        },
        cancelable: false,
      });
    }

    trackWithState(
      eventType: string,
      customPropertyId,
      customProperty: CustomProperty,
      toCreate,
      errors,
      extraProps = {},
    ) {
      const {name, displayFormula, resourceType, propertyType} = customProperty;
      const keywords = Object.keys(arbselectorDefs)
        .filter((keyword) => !keyword.startsWith(`!`))
        .map((keyword) => (keyword.endsWith(`()`) ? keyword.slice(0, -2) : keyword));
      const keywordCounts = {};
      keywords.forEach((keyword) => {
        const regex = new RegExp(`\\b${keyword}\\b`, `g`);
        // eslint-disable-next-line @typescript-eslint/prefer-regexp-exec
        keywordCounts[`# of ${keyword}`] = (displayFormula.match(regex) || []).length;
      });
      const errorCount = Object.entries(errors).length;
      const props = {
        customPropertyId,
        name,
        persisted: toCreate || customPropertyId || false,
        resourceType,
        displayFormula,
        propertyType,
        [`# of composedProperties`]: Object.entries(this.getComposedProperties()).length,
        ...(errorCount > 0 ? {errors} : {}),
        ...keywordCounts,
        ...extraProps,
      };
      this.track(eventType, props);
    }
  },
);

// See https://ace.c9.io/api/editor.html for documentation on ace editor
class Editor extends Component<{}> {
  static get observedAttributes() {
    return [`selected`, `value`];
  }
  editor: any;
  idleTimeout: ReturnType<typeof setTimeout>;

  connectedCallback() {
    const autoFocus = this.isAttributeEnabled(`auto-focus`);
    const maxLines = this.getAttribute(`max-lines`) || `Infinity`;
    const theme = this.getAttribute(`theme`) || `textmate`;
    const wrapText = this.isAttributeEnabled(`wrap-text`);

    const divElem = document.createElement(`div`);
    const textElem = document.createTextNode(``);
    divElem.appendChild(textElem);
    this.editor = ace.edit(divElem);
    this.editor.setTheme(`ace/theme/${theme}`);
    this.idleTimeout = null;

    this.editor.setOptions({
      displayIndentGuides: false,
      enableBasicAutocompletion: true,
      enableLiveAutocompletion: true,
      highlightActiveLine: true,
      maxLines,
      showFoldWidgets: false,
      showGutter: true,
      showPrintMargin: false,
      wrap: true,
    });
    this.editor.completers = [new CustomPropertyCompleter()];

    // syntax check worker must be disabled for our own use of setAnnotations to work
    this.editor.getSession().setUseWorker(false);
    this.editor.getSession().setUseWrapMode(wrapText);
    this.editor.container.style.lineHeight = 1.91;
    this.editor.renderer.setPadding(10);
    this.editor.getSession().setMode(`ace/mode/arb-selector`);
    this.editor.getSession().setTabSize(2);
    this.editor.on(`blur`, () => {
      this.dispatchEvent(
        new CustomEvent(`blur`, {
          detail: {
            height: this.editor.getSession().getScreenLength() * (this.editor.renderer.lineHeight || 1.9),
          },
        }),
      );
    });
    this.editor.on(`change`, () => {
      this.dispatchEvent(
        new CustomEvent(`change`, {
          detail: {
            value: this.editor.getValue(),
            height: this.editor.getSession().getScreenLength() * (this.editor.renderer.lineHeight || 1.9),
          },
        }),
      );
    });
    this.editor.on(`focus`, () => {
      this.dispatchEvent(new CustomEvent(`focus`, {}));
    });
    this.editor.on(`blur`, () => {
      this.dispatchEvent(
        new CustomEvent(`autocompleteChange`, {
          detail: {
            menuTooltipData: null,
            cursorTooltipData: null,
          },
        }),
      );
    });
    this.innerHTML = ``;
    this.appendChild(divElem);

    if (autoFocus) {
      this.editor.focus();
    }

    if (this.getAttribute(`value`) === ``) {
      this.editor.setValue(DEFAULT_FORMULA);
    } else {
      this.editor.setValue(this.getAttribute(`value`));
    }
    this.editor.clearSelection();

    if (!autocompleteSingleton) {
      autocompleteSingleton = new Autocomplete();
    }
    this.editor.completer = autocompleteSingleton;
    this.editor.completer.editor = this.editor;

    const popup = this.editor.completer.getPopup();
    popup.renderer.on(`afterRender`, () => {
      // item being actively hovered with mouse takes priority,
      // otherwise use item currently selected via keyboard
      const row = popup.getHoveredRow() === -1 ? popup.getRow() : popup.getHoveredRow();
      const data = popup.getData(row);
      this.dispatchEvent(
        new CustomEvent(`autocompleteChange`, {
          detail: {
            autocompleteOpen: true,
            menuTooltipData: data && data.tooltip ? data.tooltip : null,
          },
        }),
      );
    });
    popup.on(`hide`, () => {
      this.dispatchEvent(
        // clear the documentation tooltip if the autocomplete popup goes away
        new CustomEvent(`autocompleteChange`, {
          detail: {
            autocompleteOpen: false,
          },
        }),
      );
    });

    this.editor.getSession().selection.on(`changeCursor`, (_ev) => {
      const currFunc = this.getCurrentFunctionInfo(this.editor.getCursorPosition());
      let completions = null;
      if (currFunc) {
        completions = Object.keys(arbselectorDefs)
          .filter((key) => key.split(`(`)[0].toLowerCase() === currFunc.token.value.toLowerCase())
          .map((key) => ({
            header: key,
            subheader: arbselectorDefs[key][`syntax`],
            description: arbselectorDefs[key][`!doc`],
            details: [{icon: `info`, name: `Example`, value: arbselectorDefs[key][`examples`][0]}],
          }));
      }
      if (completions && completions.length > 0) {
        this.dispatchEvent(
          new CustomEvent(`autocompleteChange`, {
            detail: {
              currentAutocompleteLine: this.editor.container.getElementsByClassName(`ace_gutter-layer`)[0].children[
                currFunc.parenPos.row
              ],
              cursorTooltipData: completions[0],
            },
          }),
        );
      } else {
        this.dispatchEvent(
          new CustomEvent(`autocompleteChange`, {
            detail: {
              cursorTooltipData: null,
            },
          }),
        );
      }
    });

    this.dispatchEvent(
      new CustomEvent(`connect`, {
        detail: {
          autocompletePopup: popup.container,
          customPropertyCompleter: this.editor.completers[0],
          editor: this.editor,
        },
      }),
    );
  }

  getCurrentFunctionInfo(startingPos) {
    if (startingPos.column === 0 && this.editor.session.getLine(startingPos.row).length === 0) {
      startingPos = this.calculatePreviousPosition(startingPos);
    }
    const prevPos = this.calculatePreviousPosition(startingPos);
    const prevChar = this.editor.session.getLine(prevPos.row).charAt(prevPos.column);
    let parenPos = null;
    if (prevChar === `(`) {
      parenPos = startingPos;
    } else {
      parenPos = this.editor.session.$findOpeningBracket(`)`, startingPos, /text/);
      if (parenPos && prevChar === `)`) {
        parenPos = this.editor.session.$findOpeningBracket(`)`, parenPos, /text/);
      }
    }
    if (parenPos) {
      let ch = ` `;
      while (ch === ` `) {
        parenPos = this.calculatePreviousPosition(parenPos);
        ch = this.editor.session.getLine(parenPos.row).charAt(parenPos.column);
      }
      // Handle cases where parens aren't part of a function call, e.g.
      // STRING(A * (B + C))
      // STRING((A))
      const token = this.editor.session.getTokenAt(parenPos.row, parenPos.column);
      if (token.type === `entity.name.function`) {
        return {token, parenPos};
      } else {
        return this.getCurrentFunctionInfo(parenPos);
      }
    }
    return null;
  }

  private calculatePreviousPosition(pos) {
    if (pos.column !== 0) {
      return {
        row: pos.row,
        column: pos.column - 1,
      };
    } else if (pos.row === 0) {
      return {
        row: 0,
        column: 0,
      };
    } else {
      return {
        row: pos.row - 1,
        column: this.editor.session.getLine(pos.row - 1).length,
      };
    }
  }
}

registerMPElement(`custom-property-ace-editor`, Editor);
